import type {ReactNode} from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import {createTheme, ThemeProvider} from '@mui/material/styles'
import type {LucideIcon} from 'lucide-react'

const topicTheme = createTheme({
  palette: {
    primary: {main: '#2563eb'},
    text: {primary: '#0f172a', secondary: '#64748b'},
    divider: '#e2e8f0',
    background: {default: '#f8fafc', paper: '#ffffff'},
  },
  shape: {borderRadius: 8},
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    body1: {fontSize: '14px', lineHeight: 1.6},
    body2: {fontSize: '13px', lineHeight: 1.6},
    subtitle1: {fontSize: '16px', lineHeight: 1.5},
    subtitle2: {fontSize: '14px', lineHeight: 1.5},
    caption: {fontSize: '12px', lineHeight: 1.5},
    button: {fontSize: '13px', textTransform: 'none', fontWeight: 600},
    h6: {fontSize: '18px', fontWeight: 700, lineHeight: 1.4},
  },
  components: {
    MuiButton: {
      defaultProps: {disableElevation: true},
      styleOverrides: {root: {minHeight: 40, borderRadius: 8, padding: '8px 16px'}},
    },
    MuiTab: {
      styleOverrides: {
        root: {textTransform: 'none', fontSize: '14px', fontWeight: 600, minHeight: 52},
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '14px',
          minHeight: 44,
          backgroundColor: '#fff',
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': {borderColor: '#cbd5e1'},
          '& .MuiOutlinedInput-notchedOutline legend': {display: 'none'},
        },
      },
    },
    MuiInputLabel: {
      defaultProps: {shrink: true},
      styleOverrides: {
        root: {
          position: 'static',
          transform: 'none',
          maxWidth: '100%',
          whiteSpace: 'normal',
          overflowWrap: 'anywhere',
          fontSize: '14px',
          lineHeight: 1.5,
          fontWeight: 600,
          color: '#334155',
          marginBottom: 8,
          pointerEvents: 'auto',
          '&.MuiInputLabel-shrink': {transform: 'none', maxWidth: '100%'},
        },
      },
    },
    MuiFormLabel: {styleOverrides: {root: {fontSize: '14px', color: '#334155'}}},
    MuiFormHelperText: {
      styleOverrides: {
        root: {fontSize: '12px', lineHeight: 1.5, marginLeft: 0, marginRight: 0, marginTop: 6},
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {borderRadius: 8, fontSize: '13px'},
        message: {minWidth: 0, overflowWrap: 'anywhere'},
      },
    },
    MuiChip: {styleOverrides: {label: {fontSize: '12px'}}},
    MuiDialog: {styleOverrides: {paper: {borderRadius: 12}}},
    MuiTableCell: {
      styleOverrides: {
        root: {fontSize: '13px', padding: '14px 16px'},
        head: {fontWeight: 600, color: '#475569', backgroundColor: '#f8fafc'},
      },
    },
  },
})

export function TopicLayout({children}: {children: ReactNode}) {
  return <ThemeProvider theme={topicTheme}>{children}</ThemeProvider>
}

export function TopicFormSection({
  title,
  description,
  icon: Icon,
  children,
}: {
  title: string
  description?: string
  icon: LucideIcon
  children: ReactNode
}) {
  return (
    <Box
      component='section'
      sx={{
        minWidth: 0,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          px: {xs: 2, sm: 2.5},
          py: 2,
          bgcolor: '#f8fafc',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: 1.5,
            color: 'primary.main',
            bgcolor: '#eff6ff',
          }}
        >
          <Icon size={18} aria-hidden='true' />
        </Box>
        <Box sx={{minWidth: 0}}>
          <Typography component='h2' variant='subtitle2' fontWeight={700}>
            {title}
          </Typography>
          {description && (
            <Typography variant='body2' color='text.secondary' sx={{mt: 0.25}}>
              {description}
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{p: {xs: 2, sm: 2.5}, minWidth: 0}}>{children}</Box>
    </Box>
  )
}
